import axios from 'axios';
import config from '../config';
import { clearAccessToken, clearChoreographySubjectToken, getAccessToken } from './authStorage';
import { ensureChoreographySubjectToken } from './choreographyAuth';
import { normalizeClientVersion } from '../utils/appVersion';
import { buildLedgerQueuesApi } from './ledgerQueuesAdapter';
import { buildLedgerMovesApi } from './ledgerMovesAdapter';
import { buildBillingProfileApi } from './billingProfileAdapter';
import { buildTxHistoryApi } from './txHistoryAdapter';
import { buildRoutineDeleteRequest } from './choreographyRoutineDeleteRequest';

// App version must be semver for backend minimum-version checks.
const APP_VERSION = normalizeClientVersion(process.env.REACT_APP_VERSION);

// Create axios instance with default config
const api = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Version': APP_VERSION,
    'X-Client-Platform': 'web',
  },
});

const publicApi = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Version': APP_VERSION,
    'X-Client-Platform': 'web',
  },
});

const sanitizeDebugHeaders = (headers = {}) => {
  const sanitized = { ...headers };
  if (sanitized.Authorization) {
    sanitized.Authorization = 'Bearer <redacted>';
  }
  return sanitized;
};

const logScanTriggerDebug = (phase, details) => {
  const timestamp = new Date().toISOString();

  console.groupCollapsed(`[debug-term] /api/network/scans/trigger ${phase} ${timestamp}`);
  console.debug(details);
  console.groupEnd();

  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof window.CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent('studioaeye:network-scan-trigger-debug', {
      detail: {
        phase,
        timestamp,
        ...details,
      },
    }));
  }
};

const NETWORK_OWNER_ORG_STORAGE_KEY = 'studioaeye_network_owner_org_id';
const NETWORK_INSTALLATION_UUID_STORAGE_KEY = 'studioaeye_network_installation_uuid';

const getStoredNetworkOwnerOrgId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.sessionStorage.getItem(NETWORK_OWNER_ORG_STORAGE_KEY) || '';
};

export const setNetworkOwnerOrgId = (ownerOrgId) => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = ownerOrgId ? String(ownerOrgId) : '';
  if (normalized) {
    window.sessionStorage.setItem(NETWORK_OWNER_ORG_STORAGE_KEY, normalized);
  } else {
    window.sessionStorage.removeItem(NETWORK_OWNER_ORG_STORAGE_KEY);
  }
};

export const setNetworkInstallationUuid = (installationUuid) => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = installationUuid ? String(installationUuid) : '';
  if (normalized) {
    window.sessionStorage.setItem(NETWORK_INSTALLATION_UUID_STORAGE_KEY, normalized);
  } else {
    window.sessionStorage.removeItem(NETWORK_INSTALLATION_UUID_STORAGE_KEY);
  }
};

const withNetworkOwnerOrgScope = (params = {}) => {
  const nextParams = { ...(params || {}) };
  const explicitOwnerOrgId = nextParams.ownerOrgId || nextParams.owner_org_id || nextParams.organisationId;
  const ownerOrgId = explicitOwnerOrgId || getStoredNetworkOwnerOrgId();

  if (ownerOrgId && !explicitOwnerOrgId) {
    nextParams.ownerOrgId = ownerOrgId;
  }

  return nextParams;
};

// MCRO001: any org-scoped choreography request is a Choreo-audience request —
// with or without the /v1 prefix. A malformed (non-/v1) choreography URL is
// still a choreography request: it must carry the Choreo subject token and its
// 401 must NEVER be treated as a Core session failure (which would log the
// whole app out and bounce the user to /login over a Choreography-only glitch).
const isChoreographyRequestUrl = (url) => /\/(?:v1\/)?orgs\/[^/]+\/choreography(?:\/|$)/.test(url || '');

const SHARING_WRITE_URL = /\/v1\/resource-sharing\/scopes\/|\/choreography\/access\/|\/choreography\/routines\/[^/]+\/sharing(?:\/|$)/;

// Request interceptor to add auth token
api.interceptors.request.use(
  async (reqConfig) => {
    const isChoreographyRequest = isChoreographyRequestUrl(reqConfig.url);
    const token = isChoreographyRequest ? await ensureChoreographySubjectToken() : getAccessToken();
    if (token) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }
    // M004-F0663 (WebUI #130): every share/access WRITE carries a client request
    // id. Core logs `x-request-id`, and the sharing panels show it (and
    // console.error it) with a failure, so a failed share is traceable
    // server-side even though Core's error body carries no id of its own.
    if (SHARING_WRITE_URL.test(reqConfig.url || '') && String(reqConfig.method || 'get').toLowerCase() !== 'get' && !reqConfig.headers['X-Request-Id']) {
      reqConfig.headers['X-Request-Id'] = globalThis.crypto?.randomUUID?.() || `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
    return reqConfig;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 if it's not an unauthenticated auth attempt.
    // Login, OAuth sign-in, magic-link verification, and registration all
    // legitimately return 401 for bad credentials or unlinked identities
    // (e.g. LOGIN_METHOD_NOT_LINKED per M001-D002 §12.5.6) and must be
    // surfaced to the caller instead of triggering a redirect/token wipe.
    const requestUrl = error.config?.url || '';
    const isChoreographyRequest = isChoreographyRequestUrl(requestUrl);
    const isUnauthAuthAttempt = /\/auth\/(login|oauth\/|magic-link|register)/.test(requestUrl);
    const responseError = `${error.response?.data?.error || ''} ${error.response?.data?.message || ''}`;
    const isLedgerAppSessionChallenge = error.response?.data?.error === 'LEDGER_APP_SESSION_REQUIRED';
    const isAssuranceChallenge = /STEP_UP|SAL|ELEVATED|2SV|TWO_SV|2-step|ASSURANCE/i.test(responseError);
    if (
      error.response?.status === 401
      && !error.config?.skipAuthRedirect
      && !isChoreographyRequest
      && !isUnauthAuthAttempt
      && !isLedgerAppSessionChallenge
      && !isAssuranceChallenge
    ) {
      // Token expired or invalid
      clearAccessToken();
      window.location.href = '/login';
    }
    if (isLedgerAppSessionChallenge && typeof window !== 'undefined') {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      window.location.assign(`/api/v1/apps/ledger/auth/start?return_to=${encodeURIComponent(returnTo)}`);
    }
    if (error.response?.status === 401 && isChoreographyRequest && !error.config?.suppressChoreoReauth) {
      clearChoreographySubjectToken();
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof window.CustomEvent === 'function') {
        window.dispatchEvent(new CustomEvent('choreo:authorization-required'));
      }
    }
    return Promise.reject(error);
  }
);

// Device link API (M001-F0213 web / T-CONN-0215 / T-CONN-0228)
export const deviceLinkApi = {
  context: (userCode) => api.get('/device/link/context', { params: { user_code: userCode } }),
  approve: (userCode, organisationIds) => api.post('/device/link/approve', {
    user_code: userCode,
    organisation_ids: organisationIds,
  }),
  deny: (userCode) => api.post('/device/link/deny', { user_code: userCode }),
};

// RETIRED 2026-10-04 (owner ruling; Core #404): the central browser grant
// client (serviceLoginGrantApi, /v1/service-login-central/*) was removed with
// the /Service-Login page. Node login is RADIUS only.

// ML001-F0034 / T-LED-NODEUI-004: Core derives the actor and verifies the
// selected organisation before it asks the linked Ledger to mint a handoff.
export const ledgerNodeApi = {
  createHandoff: (ownerOrgId, installationUuid) => api.post('/v1/ledger/node-workspace/handoff', {
    ownerOrgId,
    ...(installationUuid ? { installationUuid } : {}),
  }),
  // M004-F0701 (Core #289, M001-D022-F061): SAL 3, authorised like the hand-off.
  // Answers { installationUuid, installLabel, lastSeenAt, online } per active
  // installation and nothing else (no address, token or URL).
  listInstallations: (ownerOrgId) => api.get('/v1/ledger/node-workspace/installations', { params: { ownerOrgId } }),
};

// ML001-F0005/F0025 + M004-F0304/F0305 + ML001-F0097: Core authorises both the
// organisation and target member before relaying account data or financial
// writes to Ledger. getMemberEntries pages the manager-scoped itemised member
// entries (ML001-D007 §7.7) behind the same guard chain as the summary read.
export const ledgerAccountApi = {
  getMembers: (orgId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/ledger/members`),
  getAccount: (orgId, userId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/ledger/users/${encodeURIComponent(userId)}`),
  // M004-F0702 (Core #180, M001-D022-F062): the caller's OWN unbilled occurrences.
  // `me` is the only form: the user is the session's and never a client value.
  getOwnPending: (orgId, limit) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/ledger/users/me/pending`, { params: limit ? { limit } : {} }),
  getMemberEntries: (orgId, userId, params = {}) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/ledger/users/${encodeURIComponent(userId)}/entries`, { params }),
  grantCredit: (orgId, userId, data, idempotencyKey) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/ledger/users/${encodeURIComponent(userId)}/credits`, data, {
    headers: { 'Idempotency-Key': idempotencyKey },
  }),
  // ML001-F0117 / ML001-F0119: Advice + Approval sections. The wire shape lives
  // only in ./ledgerQueuesAdapter (provisional until Spec #37 publishes rows).
  ...buildLedgerQueuesApi(api),
  // M004-F0715 / F0720 (Spec 1.4.0): moveTransaction, editAdviceAmount, billByHand. Ledger-only; never a Core read.
  ...buildLedgerMovesApi(api),
  // M004-F0730..F0732 (Spec 1.7.0, provisional): the organisation's invoice and billing settings. Wire in ./billingProfileAdapter.
  ...buildBillingProfileApi(api),
};

// M004-F0713/F0714/F0721: the transaction history reads and the credit-note write; the wire lives only in ./txHistoryAdapter.
export const txHistoryApi = buildTxHistoryApi(api);

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  loginWithGoogle: (data) => api.post('/auth/oauth/google', data),
  loginWithApple: (data) => api.post('/auth/oauth/apple', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token) => publicApi.post('/auth/email/verify', { token }),
  requestMagicLink: (email, redirectTo = '/login') =>
    api.post('/auth/magic-link/request', { email, redirect_to: redirectTo }),
  verifyMagicLink: (token) =>
    publicApi.get('/auth/magic-link/verify', {
      params: {
        token,
      },
    }),
  getLoginMethods: () => api.get('/auth/login-methods'),
  linkGoogle: (data) => api.post('/auth/login-methods/google/link', data),
  linkApple: (data) => api.post('/auth/login-methods/apple/link', data),
  unlinkLoginMethod: (provider) => api.delete(`/auth/login-methods/${provider}`),
};

export const twoSvApi = {
  getMethods: () => api.get('/auth/2sv/methods'),
  beginTotp: () => api.post('/auth/2sv/totp/begin'),
  confirmTotp: (data) => api.post('/auth/2sv/totp/confirm', data),
  enableLogin: () => api.post('/auth/2sv/enable-login'),
  satisfyChallengeTotp: (challengeId, code) => api.post(`/auth/2sv/challenges/${encodeURIComponent(challengeId)}/totp`, { code }),
  sendChallengePush: (challengeId) => api.post(`/auth/2sv/challenges/${encodeURIComponent(challengeId)}/push`),
  satisfyChallengeBackupCode: (challengeId, code) => api.post(`/auth/2sv/challenges/${encodeURIComponent(challengeId)}/backup-code`, { code }),
  regenerateBackupCodes: () => api.post('/auth/2sv/backup-codes/regenerate'),
  removeMethod: (id) => api.delete(`/auth/2sv/methods/${id}`),
  disable: (currentPassword) => api.post('/auth/2sv/disable', { currentPassword }),
  // Core-emailed security links (unauthenticated; the token IS the credential).
  // twoSv.routes.ts: POST /recovery, POST /recovery-assertion/:token/abort,
  // POST /undo-disable. publicApi so no session header is attached and the
  // 401 handling of the authenticated client never fires on a bad token.
  redeemRecovery: (token) => publicApi.post('/auth/2sv/recovery', { token }),
  abortRecovery: (token) => publicApi.post(`/auth/2sv/recovery-assertion/${encodeURIComponent(token)}/abort`),
  undoDisable: (token) => publicApi.post('/auth/2sv/undo-disable', { token }),
};

export const botSessionKeyApi = {
  listMine: () => api.get('/v1/users/me/bot-session-keys'),
  createMine: (data) => api.post('/v1/users/me/bot-session-keys', data),
  updateMine: (keyId, data) => api.patch(`/v1/users/me/bot-session-keys/${encodeURIComponent(keyId)}`, data),
  revokeMine: (keyId) => api.delete(`/v1/users/me/bot-session-keys/${encodeURIComponent(keyId)}`),
  listForUser: (userId) => api.get(`/v1/admin/users/${encodeURIComponent(userId)}/bot-session-keys`),
  createForUser: (userId, data) => api.post(`/v1/admin/users/${encodeURIComponent(userId)}/bot-session-keys`, data),
  updateForUser: (userId, keyId, data) => api.patch(`/v1/admin/users/${encodeURIComponent(userId)}/bot-session-keys/${encodeURIComponent(keyId)}`, data),
  revokeForUser: (userId, keyId) => api.delete(`/v1/admin/users/${encodeURIComponent(userId)}/bot-session-keys/${encodeURIComponent(keyId)}`),
};

const buildPublicApiUrl = (path, params = {}) => {
  const normalizedBase = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl.slice(0, -1) : config.apiBaseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${normalizedBase}${normalizedPath}`, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
};

// User API
export const userApi = {
  getProfile: () => api.get('/users/me'),
  getOrganisations: () => api.get('/v1/users/me/organisations'),
  getJoinRequests: () => api.get('/v1/users/me/join-requests'),
  requestJoin: (organisationId) => api.post('/v1/users/me/join-requests', { organisationId }),
  getApplicationAccess: () => api.get('/v1/me/application-access'),
  exportProfile: () => api.get('/users/me/export'),
  // M005-F0052 amendment (WebUI #107): the caller's own MEMBERSHIPS —
  // Core GET /users/me/organisations -> { success, data: { organisations:
  // [{ organisation_id, organisation_name, role, onboarding_status,
  // joined_at, is_primary }] } }, primary first (Core user.service.ts
  // getUserOrganisations; openapi UserOrganisationsResponse). Unlike
  // GET /organisations/mine, this never widens to every organisation for
  // organisation_view_all.
  getMyOrganisations: () => api.get('/users/me/organisations'),
  updateProfile: (data) => api.put('/users/me', data),
  changePassword: (data) => api.put('/users/password', data),
  getUsers: (params) => api.get('/users', { params }),
  getUser: (uuid) => api.get(`/users/${uuid}`),
  createUser: (data) => api.post('/auth/admin/register', data),
  updateUser: (uuid, data) => api.put(`/users/${uuid}`, data),
  deleteUser: (uuid) => api.delete(`/users/${uuid}`),
  // M004-F0652: the CALLER's own system-capability map (their role row of
  // permissions_system), readable without SAL-4 - unlike
  // GET /v1/admin/permissions/system. Core MR !966 / issue #280. The path
  // is the only place it is named in the WebUI.
  getMySystemPermissions: () => api.get('/users/me/system-permissions'),
  suspendUser: (uuid) => api.put(`/users/${uuid}`, { status: 'suspended' }),
  activateUser: (uuid) => api.put(`/users/${uuid}`, { status: 'active' }),
  verifyUserEmail: (uuid, data = {}) => api.put(`/v1/admin/users/${uuid}/email-verification`, data),
  approveUser: (uuid, data = {}) => api.put(`/v1/admin/users/${uuid}/approve`, data),
  rejectUser: (uuid, data = {}) => api.put(`/v1/admin/users/${uuid}/reject`, data),
  suspendUserAdmin: (uuid, data = {}) => api.put(`/v1/admin/users/${uuid}/suspend`, data),
  reactivateUser: (uuid, data = {}) => api.put(`/v1/admin/users/${uuid}/reactivate`, data),
  reconsiderUser: (uuid, data = {}) => api.put(`/v1/admin/users/${uuid}/reconsider`, data),
  issueRecoveryAssertion: (uuid, data = {}) => api.post(`/v1/admin/users/${encodeURIComponent(uuid)}/recovery-assertion`, data),
  // Admin remediation for a lost/compromised password is reset-email only (M001-F0265):
  // the token is generated server-side and delivered solely to the user's own
  // registered address. There is deliberately no admin set-password primitive here —
  // POST /v1/admin/users/:id/password has been removed from the platform.
  sendPasswordResetEmail: (uuid) => api.post(`/v1/admin/users/${encodeURIComponent(uuid)}/password-reset-email`),
  // This support read is deliberately a POST: a successful or denied inspection
  // writes an audit event carrying the operator's reason.
  inspectAccountOrigin: (uuid, reason) => api.post(
    `/v1/admin/users/${encodeURIComponent(uuid)}/account-origin/inspect`,
    { reason },
  ),
};

// Booking API
export const bookingApi = {
  getMyBookings: (params) => api.get('/bookings', { params }),
  getBooking: (id) => api.get(`/bookings/${id}`),
  createBooking: (data) => api.post('/bookings', data),
  updateBooking: (id, data) => api.put(`/bookings/${id}`, data),
  cancelBooking: (id) => api.post(`/bookings/${id}/cancel`),
  deleteBooking: (id, reason) => api.delete(`/bookings/${id}`, { data: { reason } }),
  // M004-F0074: Deletion lifecycle endpoints
  undoDeleteRequest: (id) => api.post(`/bookings/${id}/undo-delete`),
  // Admin endpoints
  getAllBookings: (params) => api.get('/bookings', { params }),
  setBillingCustomerFromGuest: (id, email) => api.put(`/bookings/${id}/billing-customer`, { email, mode: 'owner_guest_correction' }),
  getPendingBookings: (orgId) => api.get('/bookings/pending', { params: { org: orgId } }),
  getPendingDeletions: (orgId) => api.get('/bookings/pending-deletions', { params: { org: orgId } }),
  approveBooking: (id) => api.post(`/bookings/${id}/approve`),
  rejectBooking: (id, reason) => api.post(`/bookings/${id}/reject`, { reason }),
  approveDeleteRequest: (id) => api.post(`/bookings/${id}/approve-delete`),
  rejectDeleteRequest: (id, reason) => api.post(`/bookings/${id}/reject-delete`, { reason }),
};

// Term Booking API - M005
export const termApi = {
  getSettings: (organisationId) => api.get(`/organisations/${organisationId}/term-settings`),
  updateSettings: (organisationId, data) => api.put(`/organisations/${organisationId}/term-settings`, data),
  listTemplates: (organisationId) => api.get(`/organisations/${organisationId}/term-templates`),
  createTemplate: (organisationId, data) => api.post(`/organisations/${organisationId}/term-templates`, data),
  getTemplate: (templateId) => api.get(`/term-templates/${templateId}`),
  updateTemplate: (templateId, data) => api.put(`/term-templates/${templateId}`, data),
  deleteTemplate: (templateId) => api.delete(`/term-templates/${templateId}`),
  generateTerms: (templateId, data) => api.post(`/term-templates/${templateId}/generate`, data),
  listRoomTerms: (roomId) => api.get(`/rooms/${roomId}/terms`),
  getTermInstance: (termInstanceId) => api.get(`/term-instances/${termInstanceId}`),
  updateTermInstance: (termInstanceId, data) => api.put(`/term-instances/${termInstanceId}`, data),
  prefillTermInstance: (termInstanceId) => api.post(`/term-instances/${termInstanceId}/prefill`),
  publishTermInstance: (termInstanceId, data = {}) => api.post(`/term-instances/${termInstanceId}/publish`, data),
  refreshCalendar: (termInstanceId) => api.post(`/term-instances/${termInstanceId}/calendar-refresh`),
  getPlanner: (termInstanceId) => api.get(`/term-instances/${termInstanceId}/planner`),
  createSlot: (termInstanceId, data) => api.post(`/term-instances/${termInstanceId}/slots`, data),
  updateSlot: (slotId, data) => api.put(`/term-slots/${slotId}`, data),
  deleteSlot: (slotId) => api.delete(`/term-slots/${slotId}`),
  createSlotBooking: (slotId, data) => api.post(`/term-slots/${slotId}/bookings`, data),
  updateSlotBooking: (bookingId, data) => api.put(`/term-slot-bookings/${bookingId}`, data),
  sendBookingInvitation: (bookingId) => api.post(`/term-slot-bookings/${bookingId}/invite`),
  sendTermInvitations: (termInstanceId) => api.post(`/term-instances/${termInstanceId}/send-invitations`),
  adminConfirmBooking: (bookingId) => api.post(`/term-slot-bookings/${bookingId}/admin-confirm`),
  listMyTermBookings: () => api.get('/v1/me/term-bookings'),
  updateMyRenewal: (bookingId, data) => api.put(`/v1/me/term-bookings/${bookingId}/renewal`, data),
  acceptInvitation: (invitationId) => api.post(`/v1/me/term-invitations/${invitationId}/accept`),
  rejectInvitation: (invitationId, data = {}) => api.post(`/v1/me/term-invitations/${invitationId}/reject`, data),
};

// Notification API
export const notificationApi = {
  getNotifications: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
};

export const pushApi = {
  getSubscriptions: () => api.get('/push/subscriptions'),
  getPreferences: () => api.get('/push/preferences'),
  updatePreferences: (preferences) => api.patch('/push/preferences', { preferences }),
  sendTest: () => api.post('/push/test'),
};

export const sessionApi = {
  revokeSession: (id) => api.post(`/sessions/${id}/revoke`),
  logoutAll: () => api.post('/auth/logout-all'),
};

export const permissionApi = {
  getSystemPermissions: () => api.get('/v1/admin/permissions/system'),
  getSystemPermission: (role) => api.get(`/v1/admin/permissions/system/${encodeURIComponent(role)}`),
  updateSystemPermission: (role, data) => api.put(`/v1/admin/permissions/system/${encodeURIComponent(role)}`, data),
  getOrganisationPermissions: (organisationId) => api.get(`/v1/admin/organisations/${encodeURIComponent(organisationId)}/permissions`),
  getOrganisationPermission: (organisationId, role) => api.get(`/v1/admin/organisations/${encodeURIComponent(organisationId)}/permissions/${encodeURIComponent(role)}`),
  updateOrganisationPermission: (organisationId, role, data) => api.put(`/v1/admin/organisations/${encodeURIComponent(organisationId)}/permissions/${encodeURIComponent(role)}`, data),
  // M004-F0696 (M001-D033 §5): a super admin's edit of one app key's registration
  // declaration. The app is addressed by its approved app code; the bare key is
  // never prefixed by the client (Core applies the prefix).
  updateApplicationPermissionDefinition: (appCode, key, data) => api.put(`/v1/admin/applications/${encodeURIComponent(appCode)}/permission-catalogue/${encodeURIComponent(key)}`, data),
  getGlobalAppAccess: () => api.get('/v1/admin/permissions/apps'),
  updateGlobalAppAccess: (appCode, role, data) => api.put(`/v1/admin/permissions/apps/${encodeURIComponent(appCode)}/${encodeURIComponent(role)}`, data),
  getOrganisationAppAccess: (organisationId) => api.get(`/v1/admin/organisations/${encodeURIComponent(organisationId)}/app-access`),
  updateOrganisationAppAccess: (organisationId, appCode, role, data) => api.put(`/v1/admin/organisations/${encodeURIComponent(organisationId)}/app-access/${encodeURIComponent(appCode)}/${encodeURIComponent(role)}`, data),
  getUserAppAccess: (organisationId, userId) => api.get(`/v1/admin/organisations/${encodeURIComponent(organisationId)}/users/${encodeURIComponent(userId)}/app-access`),
  updateUserAppAccess: (organisationId, userId, appCode, data) => api.put(`/v1/admin/organisations/${encodeURIComponent(organisationId)}/users/${encodeURIComponent(userId)}/app-access/${encodeURIComponent(appCode)}`, data),
  // M001-D022-F028 / T-APPAPI-032 (Mantis #1385, Core MR !571): the
  // per-user-organisation ACTION-permission override route — verified
  // against `admin.routes.ts` on that branch, 2026-09-06. Identical
  // authority shape to the app-access pair above (permissions_system_manage
  // + SAL-4 purpose 'permissions.organisation.manage'). GET returns the raw
  // tri-state override row (`permission.<key>`: true/false/null — null
  // means "inherit from role/system"); PUT's response `permission` is the
  // fully-RESOLVED effective permission set instead (userId, organisationId,
  // role, systemRole, permissions: Record<key, boolean>) — the two are NOT
  // the same shape, do not assume the PUT response can be read the same way
  // as the GET response.
  getUserPermissions: (organisationId, userId) => api.get(`/v1/admin/organisations/${encodeURIComponent(organisationId)}/users/${encodeURIComponent(userId)}/permissions`),
  updateUserPermissions: (organisationId, userId, data) => api.put(`/v1/admin/organisations/${encodeURIComponent(organisationId)}/users/${encodeURIComponent(userId)}/permissions`, data),
};

// Schedule/Class API
export const scheduleApi = {
  getPublicSchedule: (params) => api.get('/schedule', { params }),
  getClasses: (params) => api.get('/v1/classes', { params }),
  getClass: (id) => api.get(`/v1/classes/${id}`),
  createClass: (data) => api.post('/v1/classes', data),
  updateClass: (id, data) => api.put(`/v1/classes/${id}`, data),
  deleteClass: (id) => api.delete(`/v1/classes/${id}`),
  getClassRoster: (id) => api.get(`/classes/${id}/roster`),
  markAttendance: (id, attendance) => api.post(`/classes/${id}/attendance`, { attendance }),
};

// Reusable public class copy - M004-F0318 / T-M004-0004
export const classProgrammeApi = {
  list: (params = {}) => api.get('/v1/class-programmes', { params }),
  create: (data) => api.post('/v1/class-programmes', data),
  update: (id, data) => api.put(`/v1/class-programmes/${encodeURIComponent(id)}`, data),
  updateStatus: (id, status) => api.patch(`/v1/class-programmes/${encodeURIComponent(id)}/status`, { status }),
  archive: (id) => api.delete(`/v1/class-programmes/${encodeURIComponent(id)}`),
};

// Room API
export const roomApi = {
  getRooms: (orgId, includeOrphaned = false, includeDeleted = false) => api.get('/v1/rooms', { 
    params: { 
      ...(orgId ? { org: orgId } : {}),
      ...(includeOrphaned ? { includeOrphaned: 'true' } : {}),
      ...(includeDeleted ? { includeDeleted: 'true' } : {})
    } 
  }),
  getRoom: (id) => api.get(`/v1/rooms/${id}`),
  // M004-F0765 / T-M004-0341: Core's room-scoped, source-preserving read-only device inventory.
  getDevices: (id) => api.get(`/v1/rooms/${id}/devices`),
  createRoom: (data) => api.post('/v1/rooms', data),
  updateRoom: (id, data) => api.put(`/v1/rooms/${id}`, data),
  deleteRoom: (id) => api.delete(`/v1/rooms/${id}`),
  restoreRoom: (id) => api.post(`/v1/rooms/${id}/restore`),
  uploadCoverPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('photo', file);

    return api.post(`/v1/rooms/${id}/cover-photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteCoverPhoto: (id) => api.delete(`/v1/rooms/${id}/cover-photo`),
  getRoomSchedule: (id, params) => api.get(`/v1/rooms/${id}/schedule`, { params }),
  // Climate control
  getClimate: (id) => api.get(`/v1/rooms/${id}/climate`),
  setTemperature: (id, temperature) => api.post(`/v1/rooms/${id}/climate/temperature`, { temperature }),
  setClimateMode: (id, mode) => api.post(`/v1/rooms/${id}/climate/mode`, { mode }),
  setOccupancy: (id, state) => api.post(`/v1/rooms/${id}/climate/occupancy`, { state }),
};

// WebUI #107 (2026-09-23) — see organisationApi.getMyPermissions below.
let myPermissionsCacheBustCounter = 0;

// Organisation API
export const organisationApi = {
  // Super Admin: Get all organisations
  getAll: () => api.get('/v1/organisations'),
  // Get organisations for current user
  getMine: () => api.get('/v1/organisations/mine'),
  // Get public list of organisations (for registration)
  getPublicList: () => api.get('/v1/organisations/public'),
  // Get organisation by ID
  get: (id) => api.get(`/v1/organisations/${id}`),
  // Create new organisation (Super Admin only)
  create: (data) => api.post('/v1/organisations', data),
  // Update organisation (Super Admin only)
  update: (id, data) => api.put(`/v1/organisations/${id}`, data),
  // Update elevated organisation security policy
  updateSecurityPolicy: (id, data) => api.patch(`/v1/admin/organisations/${id}/security-policy`, data),
  // Delete organisation (Super Admin only) - force=true removes all users/rooms first
  delete: (id, force = false) => api.delete(`/v1/organisations/${id}${force ? '?force=true' : ''}`),
  // Get users in organisation
  getUsers: (orgId) => api.get(`/v1/organisations/${orgId}/users`),
  // Add user to organisation
  addUser: (orgId, userId, role, isPrimary) => api.post(`/v1/organisations/${orgId}/users`, { user_id: userId, role, is_primary: isPrimary }),
  // Remove user from organisation
  removeUser: (orgId, userId) => api.delete(`/v1/organisations/${orgId}/users/${userId}`),
  // Update user role in organisation
  updateUserRole: (orgId, userId, role) => api.put(`/v1/organisations/${orgId}/users/${userId}`, { role }),
  // Set primary organisation for user
  setPrimary: (orgId, userId) => api.put(`/v1/organisations/${orgId}/users/${userId}/primary`),
  // M001-D022-F029 (Mantis #1385, Core MR !571): the caller's own resolved
  // (user > role > system precedence) permission projection for one
  // organisation. Distinct from getMine() above (org list + security
  // policy, no permission projection).
  //
  // WebUI #107 (2026-09-23): Core sends no `Cache-Control` on this route, so
  // Express's default per-response ETag makes it a conditional-GET
  // candidate — nginx access logs on production show this EXACT URL (org
  // 44, the owner's primary org) returning 304 on every single load across
  // an entire session (2026-09-22, 02:20-03:18Z). This is a personalised,
  // security-gating read (the Price plans / Studio / Calendar / Billing
  // tabs all hang off its body): it must never be served from cache, and a
  // revalidated-but-empty or non-2xx transport result must never be
  // confused with "no permission" by getMyEffectiveOrganisationPermissionsDetailed's
  // fail-closed contract below. Busting the cache key on every call (unique
  // query param + explicit no-cache request headers) removes the
  // conditional-GET path entirely, so this call is always a fresh network
  // round trip with a real body — never a 304 of any shape.
  getMyPermissions: (orgId) => api.get(`/v1/organisations/${encodeURIComponent(orgId)}/my-permissions`, {
    // A plain `Date.now()` can repeat across two calls issued inside the
    // same millisecond; the monotonic counter guarantees every call gets a
    // distinct cache key regardless of timing.
    params: { _: `${Date.now()}-${(myPermissionsCacheBustCounter += 1)}` },
    headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
  }),
};

// M004-F0616 / T-M004-0023. Booking-scoped pricing reads + Recalculate.
// Paths and response shapes verified against `pricing.routes.ts` on Core
// `origin/main` (2026-09-06): quote/plan-list are gated server-side on
// `billing.view`/`ledger_read` today, reprice on `billing.modify`/
// `invoices_manage`. Core MR 1 (#1385, M005-F0048) swaps those two capability
// keys to `pricing_plans_view`/`pricing_booking_reprice` without changing
// these paths or response shapes, so this client needs no change when it
// merges.
//
// M004-F0615 / T-M004-0022 additions (Mantis #1387), amended per lead review
// of this MR against Core's real `GET /orgs/:orgId/pricing/assignments`
// (M005-F0047/T-PRICE-024, `src/routes/pricing.routes.ts` +
// `src/services/pricing.service.ts` on Core MR !571):
//   - no `user_id` -> `listAssignmentsForOrganisation`: EVERY member's
//     CURRENT assignment in ONE query/response —
//     `{ organisation_id, members: [{ organisation_user_id, user_id,
//     current_assignment }] }`, `current_assignment` is `null` for a member
//     with no plan. This is the member-plan admin table's call — never one
//     request per member.
//   - `?user_id=N` -> `getMemberPlanAssignments`: one member's current +
//     historical assignments, newest first —
//     `{ user_id, organisation_id, assignments: [{ id, pricing_plan_id,
//     plan_name, room_id, effective_from, effective_to, assigned_by,
//     assigned_at, is_current }] }`. Used ONLY for the on-demand "Prior
//     plans" expander, never eagerly for every member.
// Neither response carries a rate/currency field on the assignment row
// itself (the rate lives on the plan, read separately via
// listPlans/getPlans) — the original assumption that this endpoint would
// return `first_hour_rate_minor`/`currency`/`room_name` per assignment was
// wrong; do not render an invented rate here.
//
// M004-F0617 (Mantis #1389) additions. Paths and request/response shapes
// verified against Core's real `pricing.routes.ts` / `pricing.service.ts`
// on `origin/main` and MR !571 (2026-09-06) — NOT the `_minor`-suffixed
// request fields this Feature's own ticket assumed:
//   - GET /plans responses carry raw `first_hour_rate_minor` /
//     `long_hour_rate_minor` integer columns (or null) straight from the DB.
//   - POST/PATCH bodies take `first_hour_rate` / `long_hour_rate` as DECIMAL
//     STRINGS in pounds (createPlanSchema/updatePlanSchema's
//     `moneyStringSchema`, e.g. "12.50") — never minor units, never a number.
export const pricingApi = {
  listPlans: (orgId, config) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/pricing/plans`, config),
  getPlans: (orgId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/pricing/plans`),
  // M004-F0680 (WebUI #108): `getBookingQuote` and `repriceBooking` are REMOVED
  // — their only callers were the retired own-booking-page price section. The
  // booking drawer speaks the one read / one write (pricePlanApi.getBookingPricing,
  // setBookingPrice below). `getBookingQuotePreview` STAYS: the Billing drawer's
  // DC-3b quote arming still reads Core's `/quote?plan_id=` dry run.
  // WEB-BKHUB-020 (owner defect ruling 2026-09-13, M005-D005 §7.3): the
  // PREVIEW read — the same quote read with an
  // optional `plan_id` dry-run query. Core computes data.preview with the
  // SAME rate engine and §13.1 applicability validation as the reprice and
  // persists NOTHING; client-side rate math is never acceptable (the rate
  // engine is authoritative). Same §13 gates as the quote read itself.
  getBookingQuotePreview: (orgId, bookingId, planId) => api.get(
    `/v1/orgs/${encodeURIComponent(orgId)}/pricing/bookings/${encodeURIComponent(bookingId)}/quote`,
    { params: { plan_id: planId } },
  ),
  // M004-F0634 §4.2 (P-3; Core ML001-D014 §5d.4 S3b): the SINGLE pricing
  // write the billing drawer's control uses — body EXACTLY ONE of
  // pricing_plan_id / custom_price_minor (XOR, the pinned M005-D005 §7.3
  // shape). The response carries the booking's pricing object PLUS a
  // `billing_pipeline` block ({posting_id, approval_status,
  // entered_from:'repriced', booking_price_quote_id}) when a posting
  // existed — the drawer updates its row from THAT block, never locally.
  // (The older `…/reprice` client method is gone with the own-booking page's
  // price section, M004-F0680; Core keeps the route as an alias for imports.)
  setBookingPrice: (orgId, bookingId, body) => api.post(
    `/v1/orgs/${encodeURIComponent(orgId)}/pricing/bookings/${encodeURIComponent(bookingId)}/price`,
    body,
  ),
  listAssignmentsForOrganisation: (organisationId) => api.get(
    `/v1/orgs/${encodeURIComponent(organisationId)}/pricing/assignments`,
  ),
  getMemberPlanAssignments: (organisationId, userId) => api.get(
    `/v1/orgs/${encodeURIComponent(organisationId)}/pricing/assignments`,
    { params: { user_id: userId } },
  ),
  assignMemberPlan: (organisationId, data) => api.post(
    `/v1/orgs/${encodeURIComponent(organisationId)}/pricing/assignments`,
    data,
  ),
  createPlan: (organisationId, data) => api.post(`/v1/orgs/${encodeURIComponent(organisationId)}/pricing/plans`, data),
  updatePlan: (organisationId, planId, data) => api.patch(`/v1/orgs/${encodeURIComponent(organisationId)}/pricing/plans/${encodeURIComponent(planId)}`, data),
  // M004-F0679 (T-M004-0027 U1, from !449): designating a room's public default.
  // Core implements M005-D004 §10.9 E2 behind the EXISTING
  // `PATCH /v1/orgs/:orgId/pricing/plans/:planId` with `is_public_default: true`
  // (M005-F0051 / T-PRICE-028: one transaction that un-sets the room's prior
  // default, writes the designate audit row, backed by the
  // `uk_plan_room_single_default` constraint) — there is deliberately no
  // separate `POST .../designate-default` route. The body is ALWAYS
  // `{ is_public_default: true }` and nothing else: designation moves the flag,
  // it never clears it (no unset action exists, U4).
  designatePublicDefault: (organisationId, planId) => api.patch(
    `/v1/orgs/${encodeURIComponent(organisationId)}/pricing/plans/${encodeURIComponent(planId)}`,
    { is_public_default: true },
  ),
  // M005-F0069 (Core plan deletion, T-PRICE-033) — the PINNED delete contract,
  // consumed here by the hub "Price plans" tab (M005-F0070, owner ruling
  // 2026-09-13). Built against in parallel with the Core lane and to be
  // re-verified against Core's merge before un-gating the live e2e delete
  // assertions: DELETE /v1/orgs/:orgId/pricing/plans/:planId
  //   → 204 (deleted, no body)
  //   | 404 { success:false, error:'PRICING_PLAN_NOT_FOUND' } (unknown or
  //     foreign-org — indistinguishable by design, M005-D004 §13.1 S3)
  //   | 403 ORG_PERMISSION_DENIED (missing pricing_plans_manage)
  //   | 409 { success:false, error:'PRICING_PLAN_IN_USE', message,
  //       detail:[{ reason, ref }] } — the plan is still referenced by a live
  //     booking quote, a member plan assignment, a room plan binding or an
  //     active pricing_grandfather_hold; `detail` names EACH blocker. The UI
  //     renders that list verbatim and never retries.
  deletePlan: (organisationId, planId) => api.delete(`/v1/orgs/${encodeURIComponent(organisationId)}/pricing/plans/${encodeURIComponent(planId)}`),
  // Part B / M004-F0619 §16a.5 — REAL contract since Core MR !590 (#109)
  // merged (verified against src/routes/pricing.routes.ts on Core main,
  // 2026-09-10): GET
  // /v1/orgs/:orgId/pricing/plans/:planId/reprice-preview?grandfather=true|false
  // — a zod .strict() QUERY schema accepting only the optional `grandfather`
  // flag (default false), returning { toReprice, held, refusedBilled }. The
  // preview classifies the plan's CURRENT live bookings, so the edit payload
  // itself is not sent; the caller's grandfather intent maps onto the query
  // flag when present, and the commit path PATCHes the plan separately
  // (PricePlansHubTab SENDS its grandfather checkbox as the real updatePlanSchema
  // `grandfather` body field — #107 defect D — and passes it to this preview as
  // `grandfather_existing_bookings`). Both key spellings map so
  // the numbers the confirm dialog shows are always the numbers the
  // confirmed choice produces.
  previewPlanEditImpact: (organisationId, planId, patch) => api.get(
    `/v1/orgs/${encodeURIComponent(organisationId)}/pricing/plans/${encodeURIComponent(planId)}/reprice-preview`,
    { params: (patch?.grandfather === true || patch?.grandfather_existing_bookings === true) ? { grandfather: true } : undefined },
  ),
};

// M004-F0618 §D.5 / Core MR !571 (#1385, M001-D022-F029, VERIFIED against
// `src/routes/organisation.routes.ts` on that branch, 2026-09-06): the
// caller's own resolved (user > role > system precedence) permission
// projection comes from the dedicated route `GET
// /organisations/:id/my-permissions`, NOT from a field added to `GET
// /organisations/mine` — that route's response shape is unchanged by !571
// (grep for `effective_permissions` on that branch returns nothing). Every
// pricing-gated control should call this one function — never re-derive the
// org lookup or read `user.role` directly — so only this function needs to
// change if the real field name/shape differs again.
//
// Fails closed: on any error (404 no membership, 4xx/5xx, network failure)
// or a malformed body missing `permissions`, this resolves to `{}` (every
// key undefined/falsy) rather than throwing or guessing `true` — a gated
// control must render as denied, never as granted, when the projection
// cannot be read.
//
// M004-F0634 §5 (2026-09-16): a fail-closed `{}` is right for VISIBILITY,
// but the !342 review established that swallowing the failure entirely is a
// defect when the hub must distinguish "no permission" from "could not
// read" — during an outage the admin must see "Could not load your
// permissions" with a retry, never the no-permission text. The DETAILED
// variant below exposes `failed`; this wrapper keeps the historic
// boolean-map contract for every existing caller, so the fix lives in ONE
// code path instead of being re-derived per component.
export const getMyEffectiveOrganisationPermissionsDetailed = async (organisationId) => {
  if (!organisationId) return { permissions: {}, failed: false };
  try {
    const response = await organisationApi.getMyPermissions(organisationId);
    const body = response?.data?.data ?? response?.data ?? {};
    return { permissions: body?.permissions || {}, failed: false };
  } catch {
    return { permissions: {}, failed: true };
  }
};

export const getMyEffectiveOrganisationPermissions = async (organisationId) => (
  await getMyEffectiveOrganisationPermissionsDetailed(organisationId)
).permissions;

// M004-F0634 §0a.3 (the ONE list; Core ML001-D014 §5d.4.1, owner-approved
// 2026-09-27, routes on Core main since 5ec27616, contract pin Core !695
// merged 2026-09-28 as 04b3f8f6) — the billing admin surface's ONLY API
// client. ONE shared identifier: the item id IS the posting id. Responses
// are the §5d.4.1 wire shapes EXACTLY — a bare ItemsRead, {results:[…]}
// for the batch, a bare history array of admin-words entries, and BARE
// {code,message} errors (no success envelope, no details). The legacy
// /billing-pipeline and /ledger-occurrences admin routes this replaced are
// REMOVED on Core main (5ec27616) — the superseded board's client below is
// gone with them. `month` is deliberately not sent: on the merged route it
// filters only the billed view, and omitting it shows the full billed view
// (the §0a.1 surface defines no month picker).
export const billingApi = {
  getItems: (organisationId, view) => api.get(
    `/v1/orgs/${encodeURIComponent(organisationId)}/billing/items`,
    { params: { view } },
  ),
  // BULK approve: ONE step-up challenge per batch — the SAME body replays
  // after step-up. Per-item outcomes; VERSION_CHANGED carries the current
  // price in admin words so the drawer re-arms (DC-3b).
  approveItems: (organisationId, items) => api.post(
    `/v1/orgs/${encodeURIComponent(organisationId)}/billing/items/approve`,
    { items },
  ),
  rejectItem: (organisationId, itemId, reason) => api.post(
    `/v1/orgs/${encodeURIComponent(organisationId)}/billing/items/${encodeURIComponent(itemId)}/reject`,
    { reason },
  ),
  retryItem: (organisationId, itemId) => api.post(
    `/v1/orgs/${encodeURIComponent(organisationId)}/billing/items/${encodeURIComponent(itemId)}/retry`,
  ),
  getItemHistory: (organisationId, itemId) => api.get(
    `/v1/orgs/${encodeURIComponent(organisationId)}/billing/items/${encodeURIComponent(itemId)}/history`,
  ),
};

// MAD001-F0081..F0084 door discovery/onboarding surface removed (owner ruling
// 2026-09-04, Mantis #1371): RealmAuthority is a protected wall and Core/WebUI
// must hold NO RealmAuthority configuration capability — no scan, no onboard,
// no door listing client. See
// docs/tests-Web/T-MAD001-0081-0084-RealmAuthority-Door-Web-Test-Plan.md for the
// struck record. Group/link status for the Access Authority page is read from
// serviceLinksApi.getContext() below — never a generic RealmAuthority URL or
// device credential.

// M004-F0324..M004-F0328 — organisation-scoped physical access administration.
// Core is the policy authority and the only browser-facing service. These calls
// must never be redirected to Mycelium or a door controller by the web client.
const accessOrganisationPath = (organisationId) => (
  `/v1/organisations/${encodeURIComponent(organisationId)}/access`
);

export const accessControlApi = {
  getOverview: (organisationId, options = {}) => api.get(
    `${accessOrganisationPath(organisationId)}/overview`,
    { signal: options.signal },
  ),
  updateSettings: (organisationId, data, idempotencyKey) => api.patch(
    `${accessOrganisationPath(organisationId)}/settings`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  createArea: (organisationId, data, idempotencyKey) => api.post(
    `${accessOrganisationPath(organisationId)}/areas`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  updateArea: (organisationId, areaId, data, idempotencyKey) => api.patch(
    `${accessOrganisationPath(organisationId)}/areas/${encodeURIComponent(areaId)}`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  deleteArea: (organisationId, areaId, data, idempotencyKey) => api.delete(
    `${accessOrganisationPath(organisationId)}/areas/${encodeURIComponent(areaId)}`,
    { data, headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  createGroup: (organisationId, data, idempotencyKey) => api.post(
    `${accessOrganisationPath(organisationId)}/groups`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  updateGroup: (organisationId, groupId, data, idempotencyKey) => api.patch(
    `${accessOrganisationPath(organisationId)}/groups/${encodeURIComponent(groupId)}`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  deleteGroup: (organisationId, groupId, data, idempotencyKey) => api.delete(
    `${accessOrganisationPath(organisationId)}/groups/${encodeURIComponent(groupId)}`,
    { data, headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  addManualGroupMember: (organisationId, groupId, userId, data, idempotencyKey) => api.put(
    `${accessOrganisationPath(organisationId)}/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}/manual`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  removeManualGroupMember: (organisationId, groupId, userId, data, idempotencyKey) => api.delete(
    `${accessOrganisationPath(organisationId)}/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}/manual`,
    { data, headers: { 'Idempotency-Key': idempotencyKey } },
  ),
};

// MCRO001 Music Choreography API. Organisation and actor authority are always
// derived by Core; the web client only selects an organisation membership.
export const choreographyApi = {
  inspectReviewInvitation: (token) => api.post('/v1/resource-access/invitations/inspect', { token }),
  registerReviewAccount: (data) => api.post('/v1/auth/resource-review/register', data),
  redeemReviewInvitation: (handle) => api.post(`/v1/resource-access/invitations/${encodeURIComponent(handle)}/redeem`),
  getResourceReview: (resourceId) => api.get(`/v1/resource-access/resources/choreography_sheet/${encodeURIComponent(resourceId)}`),
  updateOwnedReviewContribution: (type, contributionId, data) => api.patch(`/v1/resource-access/owned-contributions/${encodeURIComponent(type)}/${encodeURIComponent(contributionId)}`, data),
  withdrawOwnedReviewContribution: (type, contributionId, data) => api.post(`/v1/resource-access/owned-contributions/${encodeURIComponent(type)}/${encodeURIComponent(contributionId)}/withdraw`, data),
  getCapabilities: (orgId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/capabilities`),
  getAccess: (orgId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/access`),
  updateAccess: (orgId, userId, data) => api.put(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/access/${encodeURIComponent(userId)}`, data),
  createTrack: (orgId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/tracks`, data),
  updateTrackTiming: (orgId, trackId, data) => api.patch(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/tracks/${encodeURIComponent(trackId)}/timing`, data),
  replaceAudio: (orgId, trackId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/tracks/${encodeURIComponent(trackId)}/audio-versions`, data),
  uploadAudio: (orgId, trackId, body) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/tracks/${encodeURIComponent(trackId)}/audio-upload`, body, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  detachAudio: (orgId, trackId) => api.delete(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/tracks/${encodeURIComponent(trackId)}/audio`),
  listRoutines: (orgId, params) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines`, { params }),
  getRoutine: (orgId, routineId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}`),
  // MCRO001-F0192 / T-MCRO-215: distinct explicit-current read. Always opts in to the
  // bounded provider mode (<=2 editor / 1 viewer snapshot rows chosen by the exact
  // draft/published pointers). It never falls back to getRoutine's full history.
  getRoutineCurrent: (orgId, routineId, options = {}) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}`, { params: { revisions: 'current' }, ...(options.signal ? { signal: options.signal } : {}) }),
  getCollaboration: (orgId, routineId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/collaboration`),
  submitCollaborationUpdate: (orgId, routineId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/collaboration/updates`, data),
  resyncCollaboration: (orgId, routineId, draftId, afterSequence = 0) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/collaboration/${encodeURIComponent(draftId)}/resync`, { params: { afterSequence } }),
  checkpointCollaboration: (orgId, routineId, draftId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/collaboration/${encodeURIComponent(draftId)}/checkpoints`, data),
  createRoutine: (orgId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines`, data),
  // choreo #53 (choreo-side route contract T-MCRO-161; WebUI half T-MCRO-162)
  // (2026-09-04): creator-only DELETE of one routine,
  // the route the *-testdeploy teardown registry drains registered routines
  // through. 200 → { deletedRoutineId, deletedTrackId|null }; 404 with the
  // {"error":"CHOREOGRAPHY_NOT_FOUND"} envelope → already gone (teardown
  // treats it as success — any other 404 is a teardown FAILURE); 409
  // CHOREOGRAPHY_ROUTINE_NOT_EMPTY
  // → media/canvas links still reference the routine, a REAL failure. Plain
  // api.delete, no Idempotency-Key — the choreography surface's deletes
  // (archiveFolder, detachAudio, unlinkCanvasMedia) carry none and the
  // contract defines none; the Idempotency-Key convention is specific to the
  // resource-sharing/access surfaces (deleteMediaSharingLink et al).
  // The {"confirm": routineId} body is the choreo F0162 §6 confirmation
  // guard and only takes effect once choreo MR !209 is deployed.
  // The request shape (path suffix + confirm body) comes from the SHARED
  // builder in ./choreographyRoutineDeleteRequest — the same single source
  // the TestDeploy teardown registry's choreoDeleter imports (pipeline 8222:
  // a duplicated literal here vs the registry is exactly how the confirm
  // body drifted out of teardown deletes). Never re-inline the BODY.
  // The path is written out literally so the deploy:prod core-interface gate
  // (M004-F0684, scripts/ci/assert-core-interface-live.js) can derive it from
  // source — `choreography${request.path}` derived as DELETE
  // /orgs/{}/choreography. It is pinned equal to the builder's path, on the
  // wire, by src/services/api.deleteRoutine.test.js, so the two cannot drift.
  deleteRoutine: (orgId, routineId) => {
    const request = buildRoutineDeleteRequest(routineId);
    return api.delete(
      `/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}`,
      { data: request.body },
    );
  },
  interpretBlueprint: (orgId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/blueprint-interpretations`, data),
  updateSharing: (orgId, routineId, data) => api.patch(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/sharing`, data),
  // M001-D023 / #916 #918: Sheet access rules are served by Core's generic
  // resource-sharing engine (src/routes/resource-sharing.routes.ts), NOT by the
  // choreo service's /orgs/:orgId/choreography/* surface — that surface has no
  // GET/PUT sharing route and an unmatched path there 401s at a session-auth
  // fallback, which the api.js choreography classifier then (correctly, given
  // the URL) treats as a Choreo-token failure and loops. These calls use the
  // authorityScope/resourceType convention the choreographySheetSharingAdapter
  // registers with the engine: authorityScopeType 'organisation', authorityScopeId
  // the numeric organisation id, resourceType 'choreo_sheet' (CHOREO_SHEET_RESOURCE_TYPE).
  // The URL has no /orgs/:id/choreography segment, so the request interceptor
  // sends the normal main-app session/bearer token (not the Choreo subject
  // token) and a 401 here goes through the ordinary session-expiry path.
  getSharingAccess: (orgId, routineId) => api.get(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_sheet/${encodeURIComponent(routineId)}/access`,
  ),
  replaceSharingAccess: (orgId, routineId, data, idempotencyKey) => api.put(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_sheet/${encodeURIComponent(routineId)}/access`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  // `types` narrows the server-side ['user','group'] filter (route validates
  // against PRINCIPAL_TYPE_QUERIES = {'user','group','user,group'}). M001-D023
  // §7.3/§7.5 version 0.9: the "Add people, groups" combobox is the only
  // caller today and always passes both types in one call — the two
  // mode-switched dialogs that used to pass a single type each were removed
  // (WebUI issue #30, Mantis #1180); defaults to both for any older caller.
  searchSharingPrincipals: (orgId, routineId, query, types = ['user', 'group']) => api.get(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_sheet/${encodeURIComponent(routineId)}/principals`,
    { params: { q: query, types: types.join(',') } },
  ),
  createSharingLink: (orgId, routineId, data, idempotencyKey) => api.post(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_sheet/${encodeURIComponent(routineId)}/link`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  deleteSharingLink: (orgId, routineId, data, idempotencyKey) => api.delete(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_sheet/${encodeURIComponent(routineId)}/link`,
    { data, headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  retrySharingInvitation: (orgId, routineId, invitationId, data) => api.post(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_sheet/${encodeURIComponent(routineId)}/invitations/${encodeURIComponent(invitationId)}/retry-delivery`,
    data,
  ),
  // WebUI issue #30 / M001-D023 §9.2 (generalises the M001-F0262 media-only
  // access-explain call above to choreo_sheet): the resource-sharing engine's
  // .../access-explain route is generic per resourceType (see
  // src/routes/resource-sharing.routes.ts on Core), so this is the exact same
  // call as getMediaAccessExplain below with resourceType 'choreo_sheet'.
  getSheetAccessExplain: (orgId, routineId, subjectUuid) => api.get(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_sheet/${encodeURIComponent(routineId)}/access-explain`,
    { params: { subject: subjectUuid } },
  ),
  getSharedRoutine: (routineId) => api.get(`/v1/public/choreography/${encodeURIComponent(routineId)}`),
  getSharingLinkProjection: (token) => api.post('/v1/resource-sharing/link-projections', { token }),
  getPublicCommunity: (routineId) => api.get(`/v1/public/choreography/${encodeURIComponent(routineId)}/community`),
  saveMap: (orgId, routineId, data) => api.put(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/draft/map`, data),
  previewReanchor: (orgId, routineId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/reanchor-preview`, data),
  publish: (orgId, routineId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/publish`, data),
  restore: (orgId, routineId, revisionId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/revisions/${encodeURIComponent(revisionId)}/restore`, data),
  // T-MCRO-025: source is explicit editor tooling (auto|draft|published); an
  // omitted source stays published-only and must never infer a draft.
  getRehearsal: (orgId, routineId, count = 1, source) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/rehearsal`, { params: { count, ...(source ? { source } : {}) } }),
  getAudioAccess: (orgId, routineId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/audio-access`),
  resolveAudioGrant: (orgId, grantId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/audio-grants/${encodeURIComponent(grantId)}`),
  getAudioContent: (resolverPath) => api.get(String(resolverPath).replace(/^\/api/, ''), { responseType: 'blob', timeout: 120000 }),
  addMedia: (orgId, routineId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/media`, data),
  // MCRO001-F0030 / T-MCRO-030: Core mints the scoped upload claim (uploadToken
  // + uploadId) that the browser then presents to the same-origin video-ingest
  // service (src/services/videoIngestApi.js), never to Core again until complete.
  createVideoIngest: (orgId, routineId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/video-ingests`, data),
  completeVideoIngest: (orgId, routineId, uploadId) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/video-ingests/${encodeURIComponent(uploadId)}/complete`),
  // MCRO001-F0035 / T-MCRO-035 §7.1/§7.4: images are always small enough to
  // admit in one request (studioaeye-choreo route
  // POST /routines/:routineId/image-uploads, choreographyImageIngest.service.ts
  // admitImage()) -- a genuinely separate, simpler sibling to the
  // createVideoIngest/completeVideoIngest chunked pair above, not a third call
  // in that sequence. The whole file goes in this one multipart request; there
  // is no separate uploadChunks/finalizeThenAwaitReady/complete step for images.
  createImageUpload: (orgId, routineId, file, { title, idempotencyKey } = {}) => {
    const body = new FormData();
    body.append('file', file);
    if (title) body.append('title', title);
    body.append('idempotencyKey', idempotencyKey || `image-upload-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`);
    return api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/image-uploads`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  searchMedia: (orgId, params) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/media`, { params }),
  // MCRO001-F0030 / T-MCRO-030 Case M (GitLab WebUI #20): the durable
  // counterpart to searchMedia above -- every one of the caller's own video-ingest
  // sessions that has NOT (yet, or ever) become a registered media row: admitted,
  // uploading, validating, processing, plus failed/cancelled/expired kept visible
  // until the retention sweep actually removes them. Choreo's studioaeye-choreo MR
  // for GitLab choreo issue "video-ingest session visibility" (see
  // choreographyVideoIngestService.listPending). Never routine-filtered, same as
  // searchMedia. STRUCK by MCRO001-F0159 (MCRO001-D021 §1.1): this comment used
  // to call the pairing "org-wide" and glossed "Your Video Library" as
  // "everything the caller can see" — the library is the caller's PERSONAL set
  // now (their own uploads), and this ingest list already was caller-scoped.
  listPendingVideoIngests: (orgId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/video-ingests`),
  // MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.1): the nested media-library
  // folder tree — choreo backend MR !126 (0e5df3d1), already merged and the
  // contract these calls must match exactly, not a client invention.
  //
  // Sentinel conventions are DIFFERENT between the two `folderId`-shaped
  // params in this API, on purpose (D011 §5.1's own "implementation
  // clarification"), and every caller of these two functions must keep them
  // straight:
  //   - listFolders' `parentFolderId`: OMITTED (no query key at all) means
  //     root. There is no "root" sentinel value for it — never pass ''.
  //   - searchMedia's `folderId` (existing function above, unchanged here):
  //     KEY ABSENT means no filter at all (T-MCRO-006 regression guard);
  //     '' (present, empty) means root; a folder id means that folder.
  //     This module's Library callers always pass one of '' or an id, never
  //     omit the key, because the folder-organised Library view is always
  //     scoped to some level, never the legacy unfiltered flat list.
  listFolders: (orgId, parentFolderId) => api.get(
    `/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/folders`,
    { params: parentFolderId ? { parentFolderId } : {} },
  ),
  createFolder: (orgId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/folders`, data),
  // Rename and/or move (re-parent) in one call — mirrors PATCH /media/:mediaId's
  // metadata-edit convention (D011 §5.1). Pass only the keys that changed;
  // `patchFolder` on the server only touches fields present on the body
  // (T-MCRO-053 Case C: `{ name }` alone must not touch parentFolderId at all).
  updateFolder: (orgId, folderId, data) => api.patch(
    `/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/folders/${encodeURIComponent(folderId)}`, data,
  ),
  // Archives the folder and, server-side in one transaction, promotes its
  // direct children (subfolders and media) to its own former parent — never a
  // cascade, never an orphan-to-root (D011 §4.4). Recoverable via restoreFolder.
  archiveFolder: (orgId, folderId) => api.delete(
    `/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/folders/${encodeURIComponent(folderId)}`,
  ),
  // Un-archives the folder. Its former contents were already promoted by
  // archiveFolder and are NOT moved back (D011 §4.4) — the folder returns
  // empty. There is no list-archived-folders endpoint in the current backend
  // contract (unlike media's GET /media/trash), so this is reachable only from
  // a client that already holds the folderId — see the Library's own
  // just-archived undo notice for the one place that is true today.
  restoreFolder: (orgId, folderId) => api.post(
    `/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/folders/${encodeURIComponent(folderId)}/restore`,
  ),
  // MCRO001-F0021 (bounded slice) / T-MCRO-021 Case L "Two collections": "On this
  // canvas" — this routine's own media UNION media attached from another routine.
  // Distinct from searchMedia above (the PERSONAL "Your Video Library", MCRO001-
  // F0159 / MCRO001-D021 §1.1 — the pre-F0159 "org-wide" gloss is struck): this is
  // always scoped to one routineId and never accepts a q/free-text filter. Per
  // D021 §1.3 this pane keeps showing linked media regardless of uploader — it is
  // the one surface where another user's video legitimately appears.
  getCanvasMedia: (orgId, routineId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/media`),
  linkCanvasMedia: (orgId, routineId, mediaId) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/media/${encodeURIComponent(mediaId)}/link`),
  unlinkCanvasMedia: (orgId, routineId, mediaId) => api.delete(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/media/${encodeURIComponent(mediaId)}/link`),
  getMediaAccess: (orgId, mediaId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/${encodeURIComponent(mediaId)}/access`),
  // M001-F0262 / D013 §16.4: subject-centric "why does this subject have access?"
  // — every direct + derived (provenance-chained) grant currently in effect for
  // one subject on this media, served by Core's generic resource-sharing engine
  // (src/routes/resource-sharing.routes.ts), exactly like getSharingAccess above.
  // The URL has no /orgs/:id/choreography segment, so the request interceptor's
  // isChoreographyRequestUrl test does not match it: the normal main-app
  // session/bearer token is sent (never the Choreo subject token), and a 401
  // here goes through the ordinary session-expiry path, not the Choreo loop.
  getMediaAccessExplain: (orgId, mediaId, subjectUuid) => api.get(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_media/${encodeURIComponent(mediaId)}/access-explain`,
    { params: { subject: subjectUuid } },
  ),
  // WebUI issue #30 / M001-D023 §12.1: the video library's own sharing
  // management set, mirroring getSharingAccess/replaceSharingAccess/
  // searchSharingPrincipals/createSharingLink/deleteSharingLink/
  // retrySharingInvitation above exactly — same generic engine
  // (src/routes/resource-sharing.routes.ts), same URL shape, resourceType
  // 'choreo_media' instead of 'choreo_sheet'. choreographyMediaSharingAdapter
  // .service.ts is already registered with the engine on Core's side; only the
  // WebUI client calls were missing (the video library previously had only the
  // read-only getMediaAccess/getMediaAccessExplain above, no management surface).
  getMediaSharingAccess: (orgId, mediaId) => api.get(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_media/${encodeURIComponent(mediaId)}/access`,
  ),
  replaceMediaSharingAccess: (orgId, mediaId, data, idempotencyKey) => api.put(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_media/${encodeURIComponent(mediaId)}/access`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  searchMediaSharingPrincipals: (orgId, mediaId, query, types = ['user', 'group']) => api.get(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_media/${encodeURIComponent(mediaId)}/principals`,
    { params: { q: query, types: types.join(',') } },
  ),
  createMediaSharingLink: (orgId, mediaId, data, idempotencyKey) => api.post(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_media/${encodeURIComponent(mediaId)}/link`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  deleteMediaSharingLink: (orgId, mediaId, data, idempotencyKey) => api.delete(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_media/${encodeURIComponent(mediaId)}/link`,
    { data, headers: { 'Idempotency-Key': idempotencyKey } },
  ),
  retryMediaSharingInvitation: (orgId, mediaId, invitationId, data) => api.post(
    `/v1/resource-sharing/scopes/organisation/${encodeURIComponent(orgId)}/resources/choreo_media/${encodeURIComponent(mediaId)}/invitations/${encodeURIComponent(invitationId)}/retry-delivery`,
    data,
  ),
  getMediaContent: (orgId, mediaId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/${encodeURIComponent(mediaId)}/content`, { responseType: 'blob', timeout: 300000 }),
  // MCRO001-F0032 / T-MCRO-032 case I (studioaeye-choreo MR !52): mint a short-lived,
  // variant-locked signed stream URL for a media item. This is the ONLY Bearer-gated
  // call in the streaming-playback path — the returned url (a relative
  // /v1/orgs/.../stream?token=... path) is consumed directly as a <video>/<img>
  // src/poster attribute by the browser, which cannot attach an Authorization header,
  // so nothing downstream of this call goes through the `api` client/interceptor.
  mintMediaStreamUrl: (orgId, mediaId, variant) => api.post(
    `/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/${encodeURIComponent(mediaId)}/stream-url`,
    { variant },
  ),
  replaceMedia: (orgId, mediaId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/${encodeURIComponent(mediaId)}/replace`, data),
  // T-MCRO-006: edit a library card's descriptive metadata (title, description,
  // teachingPoint, tags) in place — no file replacement, no new media identity.
  updateMedia: (orgId, mediaId, data) => api.patch(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/${encodeURIComponent(mediaId)}`, data),
  // MCRO001-F0040 / T-MCRO-040 Case A: archiving is the RECOVERABLE state, not a
  // destruction. The row, its storage_ref, its canvas link rows and its video-ingest
  // renditions all survive for the configured retention window
  // (capabilities().mediaRetention.trashRetentionDays, default 30 days), during which
  // listTrashedMedia lists it and restoreMedia brings it back.
  //
  // This is reachable ONLY from a Library tile. The "On this canvas" pane's ✕ calls
  // unlinkCanvasMedia and must never call this — see MCRO001-F0039 / T-MCRO-039 and
  // the header of CanvasMediaPanel.js for what happened when it did.
  archiveMedia: (orgId, mediaId) => api.delete(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/${encodeURIComponent(mediaId)}`),
  // MCRO001-F0040 / T-MCRO-040 Case B: the recycle bin. Returns
  // { retentionDays, media: [{ mediaId, title, archivedAt, archivedBy, purgeAfter,
  // daysRemaining, expired, ... }] }.
  listTrashedMedia: (orgId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/trash`),
  // MCRO001-F0040 / T-MCRO-040 Case C: restore from the recycle bin. Returns the
  // media to the library and to exactly the canvases whose link rows survived.
  restoreMedia: (orgId, mediaId) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/media/${encodeURIComponent(mediaId)}/restore`),
  createDrill: (orgId, routineId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/drill-session`, data),
  // suppressChoreoReauth: community review is progressive enhancement — every
  // caller degrades gracefully when it is unavailable. The deployed Choreo
  // service may not implement this route yet, in which case the request falls
  // through to Core-session middleware and 401s even though the Choreo subject
  // token is perfectly valid; that must not wipe the token and force a
  // full-page re-authorization loop that destroys the whole Choreography page.
  getCommunity: (orgId, routineId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/community`, { suppressChoreoReauth: true }),
  updateCommunityPolicy: (orgId, routineId, data) => api.put(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/community/policy`, data),
  createCommunityThread: (orgId, routineId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/community/threads`, data),
  replyToCommunityThread: (orgId, threadId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/community/threads/${encodeURIComponent(threadId)}/replies`, data),
  addCommunityReaction: (orgId, threadId, reaction) => api.put(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/community/threads/${encodeURIComponent(threadId)}/reactions/${encodeURIComponent(reaction)}`),
  removeCommunityReaction: (orgId, threadId, reaction) => api.delete(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/community/threads/${encodeURIComponent(threadId)}/reactions/${encodeURIComponent(reaction)}`),
  setCommunityThreadState: (orgId, threadId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/community/threads/${encodeURIComponent(threadId)}/state`, data),
  moveCommunityAnchor: (orgId, threadId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/community/threads/${encodeURIComponent(threadId)}/move-anchor`, data),
  uploadCommunityMedia: (orgId, routineId, body) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/community/media-upload`, body, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }),
  addCommunityMediaShape: (orgId, mediaId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/community/media/${encodeURIComponent(mediaId)}/shapes`, data),
  moderateCommunityContribution: (orgId, contributionId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/community/contributions/${encodeURIComponent(contributionId)}/moderate`, data),
  resetCommunityContributionPresentation: (orgId, contributionId, data) => api.patch(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/community/contributions/${encodeURIComponent(contributionId)}/moderate/presentation`, data),
  // MCRO001-F0026 / T-MCRO-026 (MCRO001-D006 §2/§4/§7): revision-bound demonstration
  // timing map persistence. list() is view-only (choreography_view); the remaining
  // four require edit authority (choreography_edit_all, or choreography_edit_own +
  // caller created the routine) — the server is the sole authority, this client only
  // mirrors that check for UX. list() is sorted by priority ASC by the server.
  listDemonstrations: (orgId, routineId, revisionId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/revisions/${encodeURIComponent(revisionId)}/demonstrations`),
  createDemonstration: (orgId, routineId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/demonstrations`, data),
  updateDemonstration: (orgId, routineId, demonstrationId, data) => api.patch(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/demonstrations/${encodeURIComponent(demonstrationId)}`, data),
  reorderDemonstrationPriority: (orgId, routineId, revisionId, priorityOrder) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/revisions/${encodeURIComponent(revisionId)}/demonstrations/priority`, { priorityOrder }),
  deleteDemonstration: (orgId, routineId, demonstrationId) => api.delete(`/v1/orgs/${encodeURIComponent(orgId)}/choreography/routines/${encodeURIComponent(routineId)}/demonstrations/${encodeURIComponent(demonstrationId)}`),
};

// ML002 Booking Keeper API (Core proxy contracts from ML002-D001/D002).
export const bookkeepingApi = {
  getSummary: (orgId, params) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/bookkeeping/summary`, { params }),
  getReceipts: (orgId, params) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/bookkeeping/receipts`, { params }),
  getReceipt: (orgId, receiptId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/bookkeeping/receipts/${encodeURIComponent(receiptId)}`),
  uploadReceipt: (orgId, file, idempotencyKey) => {
    const body = new FormData();
    body.append('file', file);
    return api.post(`/v1/orgs/${encodeURIComponent(orgId)}/bookkeeping/receipts/upload`, body, {
      headers: { 'Content-Type': 'multipart/form-data', 'Idempotency-Key': idempotencyKey },
      timeout: 60000,
    });
  },
  addExpense: (orgId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/bookkeeping/expenses`, data),
  moveReceipt: (orgId, receiptId, targetOrgId) => api.post(
    `/v1/orgs/${encodeURIComponent(orgId)}/bookkeeping/receipts/${encodeURIComponent(receiptId)}/move`,
    { target_org_id: targetOrgId },
  ),
};

// M004-F0636 / M004-F0744 — the TYPED envelope routes that remain. Everything else about envelopes (list, get, create, update, close,
// reopen, delete, restore, receipt get/update/delete, purge receipts, archive, promote) goes through the CSLPT relay in
// ./envelopeApi.js (WebUI #183, design M004-F0744 section 9). Only the two binary legs stay typed: the multipart receipt upload
// (Spec core-ledger-bookkeeping 1.2.0 receipts.upload with the envelope target) and the receipt image read. Nothing else may be added
// here: envelopeApi.test.js (T-M004-ENVUX-016) scans for it.
export const envelopesApi = {
  uploadReceipt: (orgId, envelopeId, body, idempotencyKey, onUploadProgress) => {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    return api.post(
      `/v1/orgs/${encodeURIComponent(orgId)}/bookkeeping/envelopes/${encodeURIComponent(envelopeId)}/receipts`,
      body,
      {
        headers: {
          'Idempotency-Key': idempotencyKey,
          ...(isFormData ? { 'Content-Type': 'multipart/form-data' } : {}),
        },
        timeout: 60000,
        ...(typeof onUploadProgress === 'function' ? { onUploadProgress } : {}),
      },
    );
  },
  // `variant: 'thumb'` asks for the small JPEG the Ledger serves per image receipt (Spec 1.4.0, ML001-F0262); no variant is the original.
  receiptImage: (orgId, envelopeId, receiptDocumentId, variant) => api.get(
    `/v1/orgs/${encodeURIComponent(orgId)}/bookkeeping/envelopes/${encodeURIComponent(envelopeId)}/receipts/${encodeURIComponent(receiptDocumentId)}/image`,
    { responseType: 'blob', ...(variant ? { params: { variant } } : {}) },
  ),
};

// Mycelium server-link API
export const myceliumApi = {
  getLinkContext: () => api.get('/v1/mycelium/link/context'),
  /** M001-F0216 / T-CONN-0216 — operator-safe multi-node catalogue (network.view auth). */
  listNetworkNodes: (params) => api.get('/v1/mycelium/network/nodes', { params }),
  completeLink: (data) => api.post('/v1/mycelium/link', data),
  getLinkStatus: (installationUuid) => api.get(`/v1/mycelium/link/${encodeURIComponent(installationUuid)}`),
  rotateKey: (installationUuid) => api.post(`/v1/mycelium/link/${encodeURIComponent(installationUuid)}/rotate-key`),
  unlink: (installationUuid) => api.delete(`/v1/mycelium/link/${encodeURIComponent(installationUuid)}`),
};

// PanOptic server-link API
export const panoptesApi = {
  getLinkContext: () => api.get('/v1/panoptes/link/context'),
  getLinkStatus: (installationUuid) => api.get(`/v1/panoptes/link/${encodeURIComponent(installationUuid)}`),
  getOrgFeeds: (orgId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/feeds`),
  getActiveWatchRules: (orgId) => api.get(
    `/v1/orgs/${encodeURIComponent(orgId)}/feeds/watch-rules`,
    { params: { active: 1 } },
  ),
  getFeedWatchRules: (orgId, feedId) => api.get(
    `/v1/orgs/${encodeURIComponent(orgId)}/feeds/${encodeURIComponent(feedId)}/watch-rules`,
  ),
  createFeedWatchRule: (orgId, feedId, data) => api.post(
    `/v1/orgs/${encodeURIComponent(orgId)}/feeds/${encodeURIComponent(feedId)}/watch-rules`,
    data,
  ),
  deleteFeedWatchRule: (orgId, feedId, ruleId) => api.delete(
    `/v1/orgs/${encodeURIComponent(orgId)}/feeds/${encodeURIComponent(feedId)}/watch-rules/${encodeURIComponent(ruleId)}`,
  ),
  getBehaviourEvents: (orgId, params) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/panoptic/behaviour-events`, { params }),
  getTrack: (orgId, trackId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/panoptic/tracks/${encodeURIComponent(trackId)}`),
  getIdentityClusters: (orgId, params) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/panoptic/identity-clusters`, { params }),
  getIdentityCluster: (orgId, clusterId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/panoptic/identity-clusters/${encodeURIComponent(clusterId)}`),
  getFaceCandidate: (orgId, candidateId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/panoptic/face-candidates/${encodeURIComponent(candidateId)}`),
  postReviewDecision: (orgId, data) => api.post(`/v1/orgs/${encodeURIComponent(orgId)}/panoptic/review-decisions`, data),
  getBaselineSettings: (orgId) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/panoptic/baseline-settings`),
  updateBaselineSettings: (orgId, data) => api.put(`/v1/orgs/${encodeURIComponent(orgId)}/panoptic/baseline-settings`, data),
  getReviewAudit: (orgId, params) => api.get(`/v1/orgs/${encodeURIComponent(orgId)}/panoptic/review-audit`, { params }),
  getOrgFeedSnapshot: (orgId, feedId, params) => api.get(
    `/v1/orgs/${encodeURIComponent(orgId)}/feeds/${encodeURIComponent(feedId)}/snapshot`,
    { params, responseType: 'blob' },
  ),
  getOrgEventMedia: (orgId, eventId, mediaId, params) => api.get(
    `/v1/orgs/${encodeURIComponent(orgId)}/panoptic-events/${encodeURIComponent(eventId)}/media/${encodeURIComponent(mediaId)}`,
    { params, responseType: 'blob' },
  ),
};

// M004-F0321 / M004-F0319 — every organisation-linked service and its active human sessions.
export const serviceLinksApi = {
  getContext: () => api.get('/v1/service-links/context'),
};

export const twoSvOverrideApi = {
  // M001-D020 §6.3 — the authoritative override-system state. Returns 200 with
  // { enabled, active_count } whether the system is on or off, so no UI surface
  // ever has to infer "disabled" from the wording of an error response.
  getStatus: () => api.get('/v1/admin/2sv-overrides/status', { skipAuthRedirect: true }),
  getActiveCount: () => api.get('/v1/admin/2sv-overrides/count', { skipAuthRedirect: true }),
  listActive: () => api.get('/v1/admin/2sv-overrides?active=true'),
  revoke: (overrideId) => api.post(`/v1/admin/2sv-overrides/${encodeURIComponent(overrideId)}/revoke`),
};

export const publicEnquiryApi = {
  getPublicRooms: async (orgId) => {
    try {
      return await publicApi.get(`/v1/organisations/${orgId}/public-rooms`);
    } catch (error) {
      if (error.response?.status === 404 || error.response?.status === 405) {
        return publicApi.get(`/organisations/${orgId}/public-rooms`);
      }
      throw error;
    }
  },
  requestGuestOtp: (email) => publicApi.post('/auth/guest/otp/request', { email }),
  verifyGuestOtp: (email, code) => publicApi.post('/auth/guest/otp/verify', { email, code }),
  getGuestIdentityStartUrl: (provider) => buildPublicApiUrl(`/auth/guest/${provider}`),
  submitEnquiry: (guestToken, data) =>
    publicApi.post('/enquiries', data, {
      headers: {
        Authorization: `Bearer ${guestToken}`,
      },
    }),
  getMyEnquiries: (guestToken, params) =>
    publicApi.get('/enquiries/mine', {
      params,
      headers: {
        Authorization: `Bearer ${guestToken}`,
      },
    }),
};

// Climate API (M003)
export const climateApi = {
  getStatus: () => api.get('/v1/climate/status'),
  getRoomClimate: (roomId) => api.get(`/v1/rooms/${roomId}/climate`),
  setTemperature: (roomId, temp) => api.post(`/v1/rooms/${roomId}/climate/temperature`, { temperature: temp }),
  setMode: (roomId, mode) => api.post(`/v1/rooms/${roomId}/climate/mode`, { mode }),
  setOccupancy: (roomId, state) => api.post(`/v1/rooms/${roomId}/climate/occupancy`, { state }),
};

// Shutter API (M003/M010)
export const shutterApi = {
  getShutters: () => api.get('/v1/shutters'),
  getShutter: (id) => api.get(`/v1/shutters/${id}`),
  createShutter: (data) => api.post('/v1/shutters', data),
  deleteShutter: (id) => api.delete(`/v1/shutters/${id}`),
  setPosition: (id, position) => api.post(`/v1/shutters/${id}/position`, { position }),
  open: (id) => api.post(`/v1/shutters/${id}/open`),
  close: (id) => api.post(`/v1/shutters/${id}/close`),
  stop: (id) => api.post(`/v1/shutters/${id}/stop`),
  setTilt: (id, tiltPosition) => api.post(`/v1/shutters/${id}/tilt`, { tilt_position: tiltPosition }),
  getRoomShutter: (roomId) => api.get(`/v1/rooms/${roomId}/shutter`),
};

// HA Entity API (Switches & Lights — M004-F031+)
export const haEntityApi = {
  getEntities: (params) => api.get('/v1/ha-entities', { params }),
  getEntity: (id) => api.get(`/v1/ha-entities/${id}`),
  createEntity: (data) => api.post('/v1/ha-entities', data),
  updateEntity: (id, data) => api.put(`/v1/ha-entities/${id}`, data),
  deleteEntity: (id) => api.delete(`/v1/ha-entities/${id}`),
  getRoomEntities: (roomId) => api.get(`/v1/rooms/${roomId}/ha-entities`),
  // Control endpoints (switch + light)
  turnOn: (id, data) => api.post(`/v1/ha-entities/${id}/on`, data || {}),
  turnOff: (id, data) => api.post(`/v1/ha-entities/${id}/off`, data || {}),
  toggle: (id) => api.post(`/v1/ha-entities/${id}/toggle`),
  setBrightness: (id, data) => api.post(`/v1/ha-entities/${id}/brightness`, data),
  setColour: (id, data) => api.post(`/v1/ha-entities/${id}/colour`, data),
  setEffect: (id, effect) => api.post(`/v1/ha-entities/${id}/effect`, { effect }),
};

// Console API (M010)
export const consoleApi = {
  // User-facing
  getMyRooms: () => api.get('/v1/consoles/me/rooms'),

  // Admin CRUD
  getConsoles: () => api.get('/v1/consoles'),
  getConsole: (id) => api.get(`/v1/consoles/${id}`),
  createConsole: (data) => api.post('/v1/consoles', data),
  updateConsole: (id, data) => api.put(`/v1/consoles/${id}`, data),
  deleteConsole: (id) => api.delete(`/v1/consoles/${id}`),

  // Room links
  getConsoleRooms: (id) => api.get(`/v1/consoles/${id}/rooms`),
  addRoom: (id, roomId) => api.post(`/v1/consoles/${id}/rooms`, { room_id: roomId }),
  removeRoom: (id, roomId) => api.delete(`/v1/consoles/${id}/rooms/${roomId}`),

  // User assignments
  getConsoleUsers: (id) => api.get(`/v1/consoles/${id}/users`),
  addUser: (id, userId, notes) => api.post(`/v1/consoles/${id}/users`, { user_id: userId, notes }),
  removeUser: (id, userId) => api.delete(`/v1/consoles/${id}/users/${userId}`),
};

// Device/Camera API (M002)
export const deviceApi = {
  getDevices: () => api.get('/devices'),
  getDevice: (id) => api.get(`/devices/${id}`),
  getCameras: () => api.get('/cameras'),
  getCameraStream: (id) => api.get(`/cameras/${id}/stream`),
  getOccupancy: () => api.get('/occupancy'),
  getAlerts: (params) => api.get('/alerts', { params }),
  acknowledgeAlert: (id) => api.post(`/alerts/${id}/acknowledge`),
};

// Payment API (M005)
export const paymentApi = {
  getMyInvoices: () => api.get('/invoices'),
  getInvoice: (id) => api.get(`/invoices/${id}`),
  getAllInvoices: (params) => api.get('/admin/invoices', { params }),
  createPayment: (invoiceId, data) => api.post(`/invoices/${invoiceId}/pay`, data),
  getRevenueSummary: () => api.get('/admin/revenue'),
};

// System Health API
export const systemApi = {
  getHealth: () => api.get('/system/health'),
  getStats: () => api.get('/system/stats'),
};

// Network Monitor API (M017)
export const networkApi = {
  getDevices: (params) => api.get('/network/devices', { params: withNetworkOwnerOrgScope(params) }),
  getDevice: (id, params) => api.get(`/network/devices/${id}`, { params: withNetworkOwnerOrgScope(params) }),
  updateDevice: (id, data, params) => api.put(`/network/devices/${id}`, data, { params: withNetworkOwnerOrgScope(params) }),
  approveDevice: (id, data = {}, params) => api.post(`/network/devices/${id}/approve`, data, { params: withNetworkOwnerOrgScope(params) }),
  flagDevice: (id, data = {}, params) => api.post(`/network/devices/${id}/flag`, data, { params: withNetworkOwnerOrgScope(params) }),
  getDeviceHistory: (params) => api.get('/network/devices/history', { params: withNetworkOwnerOrgScope(params) }),
  updateAddressingPolicy: (id, data, params) => api.put(`/network/devices/${id}/addressing-policy`, data, { params: withNetworkOwnerOrgScope(params) }),
  getManagedInterfaces: (id, params) => api.get(`/network/devices/${id}/interfaces`, { params: withNetworkOwnerOrgScope(params) }),
  createManagedInterface: (id, data, params) => api.post(`/network/devices/${id}/interfaces`, data, { params: withNetworkOwnerOrgScope(params) }),
  updateManagedInterface: (id, interfaceId, data, params) => api.put(`/network/devices/${id}/interfaces/${interfaceId}`, data, { params: withNetworkOwnerOrgScope(params) }),
  testManagedInterface: (id, interfaceId, params) => api.post(`/network/devices/${id}/interfaces/${interfaceId}/test`, {}, { params: withNetworkOwnerOrgScope(params), timeout: 60_000 }),
  deleteManagedInterface: (id, interfaceId, params) => api.delete(`/network/devices/${id}/interfaces/${interfaceId}`, { params: withNetworkOwnerOrgScope(params) }),
  getDeviceInterfaceAccounts: (id, interfaceId, params) => api.get(`/network/devices/${id}/interfaces/${interfaceId}/accounts`, { params: withNetworkOwnerOrgScope(params) }),
  getWarnings: (params) => api.get('/network/warnings', { params: withNetworkOwnerOrgScope(params) }),
  getWarning: (id, params) => api.get(`/network/warnings/${id}`, { params: withNetworkOwnerOrgScope(params) }),
  getWarningHelp: (warningCode, params) => api.get(`/network/warning-help/${warningCode}`, { params: withNetworkOwnerOrgScope(params) }),
  acknowledgeWarning: (id, data = {}, params) => api.post(`/network/warnings/${id}/acknowledge`, data, { params: withNetworkOwnerOrgScope(params) }),
  resolveWarning: (id, data = {}, params) => api.post(`/network/warnings/${id}/resolve`, data, { params: withNetworkOwnerOrgScope(params) }),
  getDependencies: (params) => api.get('/network/dependencies', { params: withNetworkOwnerOrgScope(params) }),
  getDependencySummary: (params) => api.get('/network/dependencies/summary', { params: withNetworkOwnerOrgScope(params) }),
  getScans: (params) => api.get('/network/scans', { params: withNetworkOwnerOrgScope(params) }),
  triggerScan: async (data, params) => {
    const token = getAccessToken();
    const scopedParams = withNetworkOwnerOrgScope(params);
    const requestHeaders = {
      'Content-Type': 'application/json',
      'X-Client-Version': APP_VERSION,
      'X-Client-Platform': 'web',
    };

    if (token) {
      requestHeaders.Authorization = `Bearer ${token}`;
    }

    const requestConfig = {
      headers: {
        ...requestHeaders,
      },
      params: scopedParams,
    };

    logScanTriggerDebug('request', {
      method: 'POST',
      url: `${config.apiBaseUrl}/network/scans/trigger`,
      headers: sanitizeDebugHeaders(requestConfig.headers),
      params: scopedParams,
      body: data,
      tokenPresent: Boolean(token),
    });

    try {
      const response = await api.post('/network/scans/trigger', data, { params: scopedParams });

      logScanTriggerDebug('response', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data,
      });

      return response;
    } catch (error) {
      logScanTriggerDebug('error', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data,
      });

      throw error;
    }
  },
  getAnomalies: (params) => api.get('/network/anomalies', { params: withNetworkOwnerOrgScope(params) }),
  getAnomaly: (id, params) => api.get(`/network/anomalies/${id}`, { params: withNetworkOwnerOrgScope(params) }),
  resolveAnomaly: (id, data, params) => api.post(`/network/anomalies/${id}/resolve`, data, { params: withNetworkOwnerOrgScope(params) }),
  getLatency: (deviceId, params) => api.get(`/network/latency/${deviceId}`, { params: withNetworkOwnerOrgScope(params) }),
  getHealthSummary: (params) => api.get('/network/health/summary', { params: withNetworkOwnerOrgScope(params) }),
  getNvrHealth: (params) => api.get('/network/health/nvr', { params: withNetworkOwnerOrgScope(params) }),
  getNvrCameras: (id, params) => api.get(`/network/nvrs/${id}/cameras`, { params: withNetworkOwnerOrgScope(params) }),
  getTruenasHealth: (params) => api.get('/network/health/truenas', { params: withNetworkOwnerOrgScope(params) }),
  getUnifiHealth: (params) => api.get('/network/health/unifi', { params: withNetworkOwnerOrgScope(params) }),
  getPfsenseHealth: (params) => api.get('/network/health/pfsense', { params: withNetworkOwnerOrgScope(params) }),
  getTuyaHealth: (params) => api.get('/network/health/tuya', { params: withNetworkOwnerOrgScope(params) }),
  getCameraHealth: (params) => api.get('/network/health/cameras', { params: withNetworkOwnerOrgScope(params) }),
  getPacketStatsCurrent: (params) => api.get('/network/packet-stats/current', { params: withNetworkOwnerOrgScope(params) }),
  getPacketStatsHistoryTop10: (params) => api.get('/network/packet-stats/history/top10', { params: withNetworkOwnerOrgScope(params) }),
  getPacketStatsIpHistory: (ip, params) => api.get(`/network/packet-stats/history/${encodeURIComponent(ip)}`, { params: withNetworkOwnerOrgScope(params) }),
  getPacketStatsStatus: (params) => api.get('/network/packet-stats/status', { params: withNetworkOwnerOrgScope(params) }),
  getHaHealth: (params) => api.get('/network/health/ha', { params: withNetworkOwnerOrgScope(params) }),
  getOpenClawNetworkSummary: () => api.get('/openclaw/context/network-summary').catch(() => api.get('/v1/openclaw/context/network-summary')),
  getOpenClawWarningContext: (id) => api.get(`/openclaw/context/warning/${id}`).catch(() => api.get(`/v1/openclaw/context/warning/${id}`)),
};

// Security Cameras API (M004-D011)
export const securityCamerasApi = {
  getDevices: (params) => api.get('/v1/security/devices', { params }),
  getFeeds: (params) => api.get('/v1/security/feeds', { params }),
  getFeed: (id, params) => api.get(`/v1/security/feeds/${encodeURIComponent(id)}`, { params }),
  getSnapshot: (id, params) => api.get(`/v1/security/feeds/${encodeURIComponent(id)}/snapshot`, { params, responseType: 'blob' }),
  createFeed: (data) => api.post('/v1/security/feeds', data),
  updateFeed: (id, params, data) => api.put(`/v1/security/feeds/${encodeURIComponent(id)}`, data, { params }),
  deleteFeed: (id, params) => api.delete(`/v1/security/feeds/${encodeURIComponent(id)}`, { params }),
};

// StudioAEye Sync API (M020-F0001 / M020-F0002): server Syncthing device ID
// display and each user's own registered Syncthing device IDs.
export const syncthingApi = {
  getServerDevice: () => api.get('/v1/syncthing/server-device'),
  listMyDevices: () => api.get('/v1/me/syncthing-devices'),
  registerDevice: (deviceId, label) => api.post('/v1/me/syncthing-devices', { deviceId, label }),
  renameDevice: (id, label) => api.put(`/v1/me/syncthing-devices/${encodeURIComponent(id)}`, { label }),
  removeDevice: (id) => api.delete(`/v1/me/syncthing-devices/${encodeURIComponent(id)}`),
};

export default api;
